import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { formatEuro } from '../../../utils/formatters';

/**
 * Alert per clienti MT/AT e pagamenti irregolari.
 * Visibili sopra la tabella di confronto.
 * @param {Object} props
 * @param {Object} props.competitorData - analysis.competitor_data
 */
export const AnalysisAlerts = ({ competitorData, validation }) => {
  const showMtAlert = competitorData?.voltage_level && ['MT', 'AT'].includes(competitorData.voltage_level);
  const hasMora = competitorData?.interessi_mora > 0;
  const hasIrregular = competitorData?.pagamenti_irregolari?.length > 0;
  const showPaymentAlert = hasMora || hasIrregular;
  const totalsInvalid = validation && validation.valid === false;
  const autoCorrected = validation && validation.auto_corrected;

  if (!showMtAlert && !showPaymentAlert && !totalsInvalid && !autoCorrected) return null;

  return (
    <>
      {totalsInvalid && (
        <div className="mx-4 mt-4 p-4 bg-red-100 border-2 border-red-500 rounded-xl flex items-start gap-3" data-testid="totals-invalid-alert">
          <AlertTriangle className="w-6 h-6 text-red-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-red-900 text-sm">Totale bolletta non coerente</p>
            <p className="text-xs text-red-800 mt-1">
              La somma delle voci estratte ({formatEuro(validation.expected)}) non corrisponde al totale dichiarato in bolletta ({formatEuro(validation.actual)}).
              Delta: <strong>{formatEuro(Math.abs(validation.diff))}</strong>.
            </p>
            <p className="text-xs text-red-700 mt-2 font-medium">
              ⛔ Impossibile finalizzare. Verificare l'estrazione AI: una voce potrebbe essere errata o mancante.
            </p>
          </div>
        </div>
      )}

      {autoCorrected && (
        <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3" data-testid="auto-corrected-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900 text-xs">Correzione automatica applicata</p>
            <p className="text-[11px] text-amber-800 mt-0.5">
              {validation.auto_corrected}. Verifica che il totale ora quadri.
            </p>
          </div>
        </div>
      )}

      {showMtAlert && (
        <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-400 rounded-xl flex items-start gap-3" data-testid="mt-at-alert">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800 text-sm">Attenzione: Cliente in {competitorData.voltage_level}</p>
            <p className="text-xs text-red-700 mt-1">
              Verificare sempre le clausole contrattuali (durata, termini di disdetta, eventuali penali) prima di firmare il contratto con Energy.Lab.
            </p>
          </div>
        </div>
      )}

      {showPaymentAlert && (
        <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-400 rounded-xl" data-testid="irregular-payments-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-800 text-sm">Pagamenti irregolari rilevati</p>
              {hasMora && (
                <p className="text-xs text-red-700 mt-1">
                  Interessi di mora addebitati: <span className="font-semibold">{formatEuro(competitorData.interessi_mora)}</span>
                </p>
              )}
              {hasIrregular && (
                <div className="mt-2">
                  <p className="text-xs text-red-700 font-medium">
                    {competitorData.pagamenti_irregolari.length} fattur{competitorData.pagamenti_irregolari.length === 1 ? 'a' : 'e'} con pagamento irregolare:
                  </p>
                  <ul className="mt-1 space-y-1">
                    {competitorData.pagamenti_irregolari.map((p, idx) => (
                      <li key={idx} className="text-xs text-red-700 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                        <span>
                          {p.numero_fattura && <span className="font-medium">Fatt. {p.numero_fattura}</span>}
                          {p.importo != null && <> &mdash; {formatEuro(p.importo)}</>}
                          {p.data_scadenza && <> &mdash; scad. {p.data_scadenza}</>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[10px] text-red-600 mt-2 italic">
                Il cliente presenta morosità. Verificare la situazione debitoria prima di procedere con l'offerta.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
